import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import * as XLSX from 'xlsx';
import { HttpClient } from '../lib/httpClient';
import Logo from '../pdf/pdfImages/logo.png';

export interface LoadingListPrintRoute {
  routeId2: number;
  routeId: string;
}

const COLUMN_WIDTHS = [2, 19, 3.5, 3.5, 3.5, 4, 4, 12, 10, 7.5, 16, 15];
const PRINT_STYLES = `
  @page { size: A4 portrait; margin: 4mm 4mm 5mm; }
  * { box-sizing: border-box; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
  body { margin: 0; color: #000; font: 9pt Calibri, Arial, sans-serif; }
  .print-brand { position: fixed; top: 0; left: 0; right: 0; height: 12mm; }
  .print-brand img { display: block; width: 100%; height: 100%; object-fit: contain; object-position: left; }
  .print-layout { max-width: 202mm; margin: 0 auto; font-size: 9pt; }
  .print-layout > thead > tr > td, .print-layout > tbody > tr > td { border: 0; padding: 0; background: none; }
  .print-layout > thead > tr > td { height: 15mm; }
  .print-layout > tbody > tr { break-inside: auto; page-break-inside: auto; }
  .loading-list { width: 100%; max-width: 202mm; margin: 0 auto; }
  .loading-list + .loading-list { break-before: page; page-break-before: always; }
  h1 { display: grid; grid-template-columns: 32% minmax(0, 1fr); font-size: 14pt;
    font-weight: normal; margin: 0 0 2mm; overflow-wrap: anywhere; }
  .details { display: grid; grid-template-columns: 42% minmax(0, 1fr); gap: 1mm 0; margin: 1mm 0; }
  .details > div, .note { white-space: pre-wrap; overflow-wrap: anywhere; }
  .note { margin: 2mm 0 3mm; }
  table { width: 100%; table-layout: fixed; border-collapse: collapse; font-size: 8pt; }
  th, td { border: 0.2mm solid #000; padding: 0.5mm 0.35mm; vertical-align: top;
    white-space: pre-wrap; overflow-wrap: anywhere; word-break: break-word; }
  th { text-align: left; vertical-align: middle; font-weight: normal;
    background: #d8d8d8; border-bottom: 0.6mm solid #000; }
  thead { display: table-header-group; }
  tr { break-inside: avoid; page-break-inside: avoid; }
  .terminal td { border: 0; background: #f2f2f2; padding: 0.35mm; }
  .totals td { background: #d8d8d8; border-left: 0; border-right: 0;
    border-top: 0.6mm solid #000; text-align: center; font-weight: bold; }
  .totals td:first-child { text-align: left; font-weight: normal; }
  .drivers { margin-top: 3mm; }
  .drivers h2 { font-size: 10pt; text-align: center; margin: 0 0 1mm;
    border-top: 0.2mm solid #000; border-bottom: 0.6mm solid #000; background: #f2f2f2; }
  .driver-entry { break-inside: avoid; page-break-inside: avoid; margin: 1mm 0 3mm; }
  .driver-entry .details { margin: 0.5mm 0; }
  @media screen { body { padding: 4mm; background: #eee; }
    .print-brand { position: static; max-width: 202mm; margin: 0 auto 3mm; }
    .print-layout > thead { display: none; }
    .loading-list { background: white; margin-bottom: 4mm; } }
`;

function SheetLabel({ text }: { text: string }) {
  const colon = text.indexOf(':');
  return colon < 0 ? (
    <>{text}</>
  ) : (
    <>
      <strong>{text.slice(0, colon + 1)}</strong>
      {text.slice(colon + 1)}
    </>
  );
}

function cellText(sheet: XLSX.WorkSheet, row: number, column: number): string {
  const cell = sheet[XLSX.utils.encode_cell({ r: row, c: column })];
  return cell ? XLSX.utils.format_cell(cell) : '';
}

function LoadingListSheet({ sheet }: { sheet: XLSX.WorkSheet }) {
  if (!sheet['!ref']) {
    throw new Error('The loading-list workbook is empty.');
  }
  const range = XLSX.utils.decode_range(sheet['!ref']);
  const totalRow = Object.keys(sheet).find(
    (address) =>
      address.startsWith('A') &&
      sheet[address]?.f?.startsWith('"Utilization: "'),
  );
  if (!totalRow) {
    throw new Error('The loading-list workbook has no totals row.');
  }
  const totalRowIndex = XLSX.utils.decode_cell(totalRow).r;
  const itemRows = Array.from(
    { length: totalRowIndex - 8 },
    (_, index) => index + 8,
  );
  // ExcelJS exports formulas without cached results; browsers cannot evaluate them.
  const totals = [2, 3, 4, 5].map((column) => {
    let total = 0;
    let correction = 0;
    // Compensated summation keeps fractional pallet spaces accurate for utilization.
    itemRows.forEach((row) => {
      const cell = sheet[XLSX.utils.encode_cell({ r: row, c: column })];
      const value = (typeof cell?.v === 'number' ? cell.v : 0) - correction;
      const nextTotal = total + value;
      correction = nextTotal - total - value;
      total = nextTotal;
    });
    return Number(total.toPrecision(15));
  });
  const capacity = Number(
    cellText(sheet, 2, 5).replace('CAPACITY:', '').trim(),
  );
  const utilization =
    capacity > 0 ? `${Math.round((totals[2] / capacity) * 100)} %` : 'N/A';
  const driverRows = Array.from(
    { length: range.e.r - totalRowIndex },
    (_, index) => index + totalRowIndex + 1,
  ).filter((row) => [0, 1, 5].some((column) => cellText(sheet, row, column)));
  const driverHeading = driverRows.find((row) =>
    cellText(sheet, row, 0).startsWith('Driver Details'),
  );
  const driverDetailRows = driverRows.filter((row) => row !== driverHeading);
  const driverEntries = Array.from(
    { length: Math.ceil(driverDetailRows.length / 2) },
    (_, index) => driverDetailRows.slice(index * 2, index * 2 + 2),
  );

  return (
    <section className="loading-list">
      <h1>
        <span>{cellText(sheet, 1, 0)}</span>
        <span>{cellText(sheet, 1, 2)}</span>
      </h1>
      <div className="details">
        {[2, 3, 4].map((row) => (
          <React.Fragment key={row}>
            <div>
              <SheetLabel text={cellText(sheet, row, 0)} />
            </div>
            <div>
              <SheetLabel text={cellText(sheet, row, 5)} />
            </div>
          </React.Fragment>
        ))}
      </div>
      <div className="note">
        <SheetLabel text={cellText(sheet, 5, 0)} />
      </div>
      <table>
        <colgroup>
          {COLUMN_WIDTHS.map((width, index) => (
            <col key={index} style={{ width: `${width}%` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {COLUMN_WIDTHS.map((_, column) => (
              <th key={column} scope="col">
                {cellText(sheet, 7, column)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {itemRows.map((row) => {
            const firstCell = sheet[`A${row + 1}`];
            const isItem = typeof firstCell?.v === 'number';
            return (
              <tr key={row} className={isItem ? undefined : 'terminal'}>
                {isItem ? (
                  COLUMN_WIDTHS.map((_, column) => (
                    <td key={column}>{cellText(sheet, row, column)}</td>
                  ))
                ) : (
                  <td colSpan={12}>{cellText(sheet, row, 0)}</td>
                )}
              </tr>
            );
          })}
          <tr className="totals">
            <td colSpan={2}>Utilization: {utilization}</td>
            {totals.map((total, index) => (
              <td key={index}>
                {XLSX.utils.format_cell({
                  t: 'n',
                  v: total,
                  z: sheet[
                    XLSX.utils.encode_cell({ r: totalRowIndex, c: index + 2 })
                  ]?.z,
                })}
              </td>
            ))}
            <td colSpan={6} />
          </tr>
        </tbody>
      </table>
      <div className="drivers">
        {driverHeading !== undefined ? (
          <h2>{cellText(sheet, driverHeading, 0)}</h2>
        ) : null}
        {driverEntries.map((entry) => (
          <div className="driver-entry" key={entry[0]}>
            {entry.map((row) => (
              <div className="details" key={row}>
                <div>
                  <SheetLabel
                    text={[cellText(sheet, row, 0), cellText(sheet, row, 1)]
                      .filter(Boolean)
                      .join(' ')}
                  />
                </div>
                <div>
                  <SheetLabel text={cellText(sheet, row, 5)} />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

export function buildLoadingListPrintDocument(
  workbooks: XLSX.WorkBook[],
  title: string,
): string {
  return (
    '<!DOCTYPE html>' +
    renderToStaticMarkup(
      <html>
        <head>
          <meta charSet="utf-8" />
          <title>{title}</title>
          <style dangerouslySetInnerHTML={{ __html: PRINT_STYLES }} />
        </head>
        <body>
          <header className="print-brand">
            <img
              src={new URL(Logo, window.location.href).href}
              alt="Bring - Finding New Ways"
            />
          </header>
          <table className="print-layout" role="presentation">
            {/* Repeating this space keeps the fixed banner clear on every page. */}
            <thead>
              <tr>
                <td aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  {workbooks.map((workbook, index) => {
                    const sheet = workbook.Sheets[workbook.SheetNames[0]];
                    if (!sheet) {
                      throw new Error(
                        'The loading-list workbook has no worksheet.',
                      );
                    }
                    return <LoadingListSheet key={index} sheet={sheet} />;
                  })}
                </td>
              </tr>
            </tbody>
          </table>
        </body>
      </html>,
    )
  );
}

export async function printLoadingLists(
  routes: LoadingListPrintRoute[],
  httpClient: HttpClient,
  messages: { title: string; preparing: string; popupBlocked: string },
): Promise<void> {
  if (!routes.length) {
    throw new Error('Select at least one loading list to print.');
  }
  // Open during the click event, before fetching, to avoid popup blocking.
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    throw new Error(messages.popupBlocked);
  }
  printWindow.document.title = messages.title;
  printWindow.document.body.textContent = messages.preparing;
  try {
    const uniqueRoutes = Array.from(
      new Map(routes.map((route) => [route.routeId2, route])).values(),
    );
    const workbooks = await Promise.all(
      uniqueRoutes.map(async (route) => {
        const response = await httpClient.downloadLoadingListBlob({
          routeId: route.routeId2,
        });
        return XLSX.read(await response.data.arrayBuffer(), {
          type: 'array',
          sheetStubs: true,
          cellNF: true,
        });
      }),
    );
    if (printWindow.closed) {
      throw new Error('The loading-list print window was closed.');
    }
    const html = buildLoadingListPrintDocument(workbooks, messages.title);
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    await printWindow.document.fonts.ready;
    await Promise.all(
      Array.from(printWindow.document.images).map((image) =>
        image.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              // A missing or slow logo must not block printing the loading lists.
              const timeout = window.setTimeout(resolve, 3000);
              const done = () => {
                window.clearTimeout(timeout);
                resolve();
              };
              image.addEventListener('load', done, { once: true });
              image.addEventListener('error', done, { once: true });
            }),
      ),
    );
    await new Promise<void>((resolve) => {
      const timeout = window.setTimeout(resolve, 100);
      printWindow.requestAnimationFrame(() => {
        window.clearTimeout(timeout);
        resolve();
      });
    });
    printWindow.addEventListener('afterprint', () => printWindow.close(), {
      once: true,
    });
    printWindow.focus();
    printWindow.print();
  } catch (error) {
    printWindow.close();
    throw error;
  }
}
